import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

// Fleet Finance access rights (backend hand-off 29 Oct 2026).
// One row per Access Rights switch pair; `routes` are the screens it opens.
// Manage implies View. `view: null` / `manage: null` = no such switch.
export const FF_RIGHTS = [
  {
    label: "Finance Settings",
    view: "rights_view_fleet_finance_settings",
    manage: "rights_manage_fleet_finance_settings",
    routes: ["/FinanceSettings", "/CurrencyRates", "/IntegrationTargets"],
  },
  {
    label: "Chart of Accounts",
    view: "rights_view_chart_of_accounts",
    manage: "rights_manage_chart_of_accounts",
    routes: ["/ChartOfAccounts"],
  },
  {
    label: "Journal Entries",
    view: "rights_view_journal_entries",
    manage: "rights_manage_journal_entries",
    routes: ["/JournalEntries", "/TrialBalance"],
  },
  {
    label: "TCO / CPK",
    view: "rights_view_fleet_tco",
    manage: "rights_manage_fleet_tco",
    routes: ["/TcoCpk"],
  },
  {
    label: "Asset Valuation",
    view: "rights_view_asset_valuation",
    manage: "rights_manage_asset_valuation",
    routes: ["/AssetValuation"],
  },
  {
    label: "Depreciation",
    view: "rights_view_depreciation",
    manage: "rights_manage_depreciation",
    routes: ["/Depreciation"],
  },
  {
    label: "Leases",
    view: "rights_view_fleet_leases",
    manage: "rights_manage_fleet_leases",
    routes: ["/Leases"],
  },
  {
    label: "Fleet Audit",
    view: "rights_view_fleet_audit",
    manage: "rights_manage_fleet_audit",
    routes: ["/FleetAudit"],
  },
  {
    label: "Financial Statements",
    view: "rights_view_financial_statements",
    manage: null,
    routes: ["/FinancialStatements"],
  },
  {
    label: "Sandbox",
    view: null,
    manage: "rights_manage_fleet_finance_sandbox",
    routes: ["/FleetFinanceSandbox"],
  },
];

export const FF_RIGHT_KEYS = FF_RIGHTS.flatMap((r) =>
  [r.view, r.manage].filter(Boolean)
);

// Order the sidebar lists the screens in; the group link opens the first allowed.
export const FF_MENU_ORDER = [
  "/ChartOfAccounts",
  "/JournalEntries",
  "/TrialBalance",
  "/TcoCpk",
  "/AssetValuation",
  "/Depreciation",
  "/FleetAudit",
  "/FinancialStatements",
  "/IntegrationTargets",
  "/FinanceSettings",
  "/CurrencyRates",
  "/FleetFinanceSandbox",
];

// Same as the other modules: follow the switches for every role, the owner
// included (the backend sends the owner's keys as 1 unless switched off).
const granted = (rights, key) => !!key && Number(rights?.[key]) === 1;

const rowForRoute = (route) => {
  const r = (route || "").toLowerCase();
  return FF_RIGHTS.find((row) =>
    row.routes.some((p) => p.toLowerCase() === r)
  );
};

export const isFfRoute = (route) => !!rowForRoute(route);

export const ffCanManage = (rights, route) =>
  granted(rights, rowForRoute(route)?.manage);

export const ffCanView = (rights, route) => {
  const row = rowForRoute(route);
  return !!row && (granted(rights, row.view) || granted(rights, row.manage));
};

export const ffAllowedRoutes = (rights) =>
  FF_RIGHTS.flatMap((row) => row.routes).filter((route) =>
    ffCanView(rights, route)
  );

export const ffFirstRoute = (rights) =>
  FF_MENU_ORDER.find((route) => ffCanView(rights, route)) || null;

// Rights for the screen being shown: { canView, canManage }.
export const useFfAccess = (route) => {
  const rights = useSelector((state) => state.auth.accessRights);
  const { pathname } = useLocation();
  const r = route || pathname;
  return { canView: ffCanView(rights, r), canManage: ffCanManage(rights, r) };
};
